<template>
  <div class="mint-product-details-component">
    <div v-if="selectedProduct">
      <table class="table table-fixed table-bordered" style="margin-bottom: 0;">
        <tbody>
        <tr>
          <th>Rentevastperiode</th>
          <th v-for="ltv in ltvs">
            <span v-if="ltv.nhg">
              {{ formatNHGHeader(ltv) }}
            </span>
            <span v-else>
              &leq; {{ formatPercentage(ltv.ltvHigh, 0) }} MW
            </span>
          </th>
        </tr>
        <tr v-for="(interestRateGroup, fixedInterestDuration) in interestRates">
          <td>
            {{ formatFixedInterestDuration(fixedInterestDuration) }}
          </td>
          <td v-for="ltv in ltvs">
            <a-tooltip placement="top">
              <template slot="title">
                <span>Rente vanaf: {{ getStartingDateByLTVHigh(interestRateGroup, ltv.ltvHigh) }}</span>
              </template>
              {{ formatPercentage(getInterestRateByLTVHigh(interestRateGroup, ltv.ltvHigh)) }}
            </a-tooltip>
          </td>
        </tr>
        </tbody>
      </table>

      <div v-if="interestModifiers && hasAppliedInterestModifiers" style="padding: 1.5rem;">
        <h4>Kortingen</h4>
        <p>De volgende kortingen zijn verwerkt in de tarieven hierboven:</p>
        <ol>
          <li v-for="(interestModifierPercentage, interestModifierKey) in appliedInterestModifiers">
            {{ formatPercentage(interestModifierPercentage) }}
            korting op de vaste rente op basis van
            {{ formatInterestModifierName(interestModifierKey) }}
          </li>
        </ol>
      </div>
    </div>
    <table class="table table-fixed table-bordered" v-else style="margin-bottom: 0;">
      <tbody>
      <tr>
        <th>Rentevastperiode</th>
        <th><span>
          NHG
        </span></th>
        <th><span>
          &leq; 60% MW
        </span></th>
        <th><span>
          &leq; 70% MW
        </span></th>
        <th><span>
          &leq; 80% MW
        </span></th>
        <th><span>
          &leq; 90% MW
        </span></th>
        <th><span>
          &leq; 95% MW
        </span></th>
        <th><span>
          &leq; 106% MW
        </span></th>
      </tr>
      <tr v-for="tr in 15" :key="'tr' + tr">
        <td v-for="td in 8" :key="'td' + td">
          <Skeleton height="22" width="60px" :loading="true"
                    style="display: inline-block;width: 100%;"></Skeleton>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {Skeleton} from "vue-loading-skeleton";
import mintApi from '~/api/mint';
import {get} from 'lodash';
import moment from 'moment';

export default {
  name: "MINTProductDetailsComponent",
  components: {Skeleton},
  props: {
    product: {
      type: Object,
      required: true,
    },
    searchParams: {
      type: Object,
      required: false,
      default: function () {
        return {};
      }
    }
  },
  data() {
    return {
      results: {
        loading: false,
        overview: [],
        sorting: {
          enabled: true,
          type: 'rate',
          direction: 'asc',
          rate: 240
        }
      },
      productValue: {
        interestRates: []
      },
      interestModifiers: {
        value: [],
        values: []
      },
      products: null,
      selectedRepaymentType: null,
      loading: true,
    }
  },
  computed: {
    selectedProduct: function () {
      if (this.products !== null) {
        for (let i in this.products) {
          let product = this.products[i];

          if (product.repaymentMethod, this.selectedRepaymentType) {
            return product;
          }
        }

        return null;
      }

      return null;
    },
    interestRates: function () {
      if (this.selectedProduct) {
        return this.selectedProduct.interestRates;
      }
      return [];
    },
    fixedInterestDurations: function () {
      if (this.interestRates) {
        return Object.keys(this.interestRates);
      }

      return [];
    },
    hasAppliedInterestModifiers: function () {
      return Object.keys(this.appliedInterestModifiers).length > 0;
    },
    appliedInterestModifiers: function () {
      let appliedInterestModifiers = {};

      for (let months in this.interestRates) {
        let interestRatesGroup = this.interestRates[months];
        interestRatesGroup.forEach(interestRate => {
          interestRate.availableInterestModifiers.forEach(availableInterestModifier => {
            if (!appliedInterestModifiers.hasOwnProperty(availableInterestModifier.interestModifierType)) {
              appliedInterestModifiers[availableInterestModifier.interestModifierType] = availableInterestModifier.value;
            }
          });
        });
      }

      return appliedInterestModifiers;
    },
    ltvs: function () {
      if (this.interestRates) {
        let ltvs = [];

        for (let fixedInterestDuration in this.interestRates) {
          for (let i in this.interestRates[fixedInterestDuration]) {
            let interestRate = this.interestRates[fixedInterestDuration][i];

            if (!ltvs.find(item => item.ltvHigh === interestRate.ltvHigh)) {
              ltvs.push(interestRate);
            }
          }
        }

        return ltvs;
      }

      return [];
    },
  },
  methods: {
    calculateLtv() {
      this.ltv.value = this.principalSum.value / this.marketValue.value;
    },
    selectProduct(product) {
      var index = this.selectedProducts.values.indexOf(product.uniqueKey);

      if (index === -1) {
        this.selectedProducts.values.push(product.uniqueKey);
      } else {
        Vue.set(this.selectedProducts.values, this.selectedProducts.values.splice(index, 1));
      }

      this.$emit('product-selected', product);
    },
    productIsSelected(product) {
      return this.selectedProducts.values.indexOf(product.uniqueKey) >= 0;
    },
    setSorting(rate, type, order) {
      this.results.sorting.type = type;
      this.results.sorting.direction = order;
      this.results.sorting.rate = rate;
    },
    getStartingDateByLTVHigh(interestRates, ltvHigh) {
      for (let i in interestRates) {
        let interestRate = interestRates[i];

        if (interestRate.ltvHigh === ltvHigh) {
          return moment(interestRate.startingDate, moment.ISO_8601).format('D/MM/YYYY');
        }
      }

      return null;
    },
    getInterestRateByLTVHigh(interestRates, ltvHigh) {
      for (let i in interestRates) {
        let interestRate = interestRates[i];

        if (interestRate.ltvHigh === ltvHigh) {
          return interestRate.rate;
        }
      }

      return null;
    },
    formatInterestModifierName(key) {
      return this.interestModifiers.values[key];
    },
    formatPercentage(value, decimals = 2) {
      if (value == null) {
        return '-';
      }

      return this.lodash.round((value * 100), 6).toFixed(decimals) + '%';
    },
    formatFixedInterestDuration(duration) {
      duration = parseInt(duration);

      if (duration === 1) {
        return 'Variabel';
      }

      if (duration < 12) {
        return duration + ' maanden';
      }

      let years = (duration / 12);

      return years + ' jaar';
    },
    formatNHGHeader(interestRate) {
      if (interestRate.ltvHigh === 9999) {
        return 'NHG >';
      }

      return 'NHG < ' + this.formatPercentage(interestRate.ltvHigh, 0)
    },
    formatStartingDate(product, rate) {
      var value = this.lodash.get(product, 'interestRates.' + rate + '.startingDate', null)

      if (value == null) {
        return 'onbekend';
      }

      return moment(value).format('DD-MM-YYYY')

    },
  },
  mounted() {
    this.selectedRepaymentType = this.product.repaymentMethod;

    mintApi.interstRates({
      search: true,
      output: 'product_matrix',
      hdnCompanyProductCodes: [this.product.hdnCodeLeningMijType],
      repaymentMethods: [],
      interestPeriods: [],
      loanToValues: [0.6, 0.7, 0.8, 0.9, 0.95, 1.05, 9999],
      ...this.searchParams
    }).then((response) => {
      this.products = response.data;
      this.interestModifiers.values = response.meta.interestModifiers.values;
      this.interestModifiers.value = response.meta.interestModifiers.default;
    }).finally(() => {
      this.results.loading = false;
    });
  }
}
</script>

<style scoped>
.mint-product-details-component table th,
.mint-product-details-component table td {
  text-align: center;
}
</style>
